import {
  Modal,
  Card,
  Typography,
  IconButton,
  Box,
  Grid,
  Divider,
  Chip,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

const ViewDetailsModal = ({ open, setOpen, attempt }) => {
  const handleClose = () => {
    setOpen(false);
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 0:
        return <Chip label="Pending" color="warning" />;
      case 1:
        return <Chip label="Completed" color="success" />;
      case 2:
        return <Chip label="Failed" color="error" />;
      case 3:
        return <Chip label="Cancelled" color="default" />;
      default:
        return <Chip label="Unknown" />;
    }
  };

  const getProofStatusChip = (proofStatus) => {
    switch (proofStatus) {
      case "not_required":
        return <Chip label="Not Required" size="small" />;
      case "pending":
        return <Chip label="Pending" color="warning" size="small" />;
      case "approved":
        return <Chip label="Approved" color="success" size="small" />;
      case "rejected":
        return <Chip label="Rejected" color="error" size="small" />;
      case "not_submitted":
        return <Chip label="Not Submitted" color="default" size="small" />;
      case "partially_rejected":
        return <Chip label="Partially Rejected" color="warning" size="small" />;
      default:
        return <Chip label={proofStatus} size="small" />;
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "scroll",
      }}
    >
      <Card
        sx={{
          width: { xs: "95%", sm: "90%", md: 800 },
          maxHeight: "90vh",
          overflow: "auto",
          p: 3,
          m: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5">User Offer Attempt Details</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* User Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            User Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                User Email
              </Typography>
              <Typography variant="body1">{attempt?.user_id?.email || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                User Name
              </Typography>
              <Typography variant="body1">{attempt?.user_id?.name || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Publisher User ID
              </Typography>
              <Typography variant="body1">{attempt?.p_user_id || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Publisher
              </Typography>
              <Typography variant="body1">{attempt?.publisher_id?.name || "N/A"}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Offer Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Offer Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {attempt?.offer_id?.image?.base && (
                  <Avatar
                    src={attempt?.offer_id?.image?.base}
                    sx={{ width: 60, height: 60, borderRadius: 2 }}
                    variant="rounded"
                  />
                )}
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {attempt?.offer_id?.name || "N/A"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Payout: {attempt?.offer_id?.total_payout_amount || 0} coins
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Partner
              </Typography>
              <Typography variant="body1">{attempt?.partner_id?.name || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Category
              </Typography>
              <Typography variant="body1">{attempt?.category || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Package Name
              </Typography>
              <Typography variant="body1">{attempt?.package_name || "N/A"}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Attempt Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Attempt Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Transaction ID
              </Typography>
              <Typography variant="body1">{attempt?.trans_id || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Box>{getStatusChip(attempt?.status)}</Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Payout Received
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="success.main">
                {attempt?.payout_received || 0} coins
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Publisher Payout
              </Typography>
              <Typography variant="body1">{attempt?.publisher_payout || 0} coins</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Started At
              </Typography>
              <Typography variant="body1">
                {moment(attempt?.clicked_on).format("DD-MM-YYYY hh:mm:ss A")}
              </Typography>
            </Grid>
            {attempt?.completed_on && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Completed At
                </Typography>
                <Typography variant="body1">
                  {moment(attempt?.completed_on).format("DD-MM-YYYY hh:mm:ss A")}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Click IP
              </Typography>
              <Typography variant="body1">{attempt?.click_ip || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Device ID
              </Typography>
              <Typography variant="body1">{attempt?.device_id || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Visitor ID
              </Typography>
              <Typography variant="body1">{attempt?.visitor_id || "N/A"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Is Bonus
              </Typography>
              <Chip
                label={attempt?.is_bonus ? "Yes" : "No"}
                color={attempt?.is_bonus ? "success" : "default"}
                size="small"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Postback Data */}
        {attempt?.postback_data && attempt.postback_data.length > 0 && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Postback Data ({attempt.postback_data.length})
              </Typography>
              {attempt.postback_data.map((pb, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 1,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Goal Name
                      </Typography>
                      <Typography variant="body2">{pb.goal_name || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Payout Amount
                      </Typography>
                      <Typography variant="body2">{pb.payout_amount || 0} coins</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Reward ID
                      </Typography>
                      <Typography variant="body2">{pb.reward_id || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Received On
                      </Typography>
                      <Typography variant="body2">
                        {pb.received_on ? moment(pb.received_on).format("DD-MM-YYYY hh:mm A") : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Internal Rewards */}
        {attempt?.internal_rewards && attempt.internal_rewards.length > 0 && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Internal Rewards ({attempt.internal_rewards.length})
              </Typography>
              {attempt.internal_rewards.map((reward, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 1,
                    bgcolor: "#e3f2fd",
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Level
                      </Typography>
                      <Typography variant="body2">{reward.level || "N/A"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Reward Payout
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {reward.reward_payout || 0} coins
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Is Claimed
                      </Typography>
                      <Box>
                        <Chip
                          label={reward.is_claimed ? "Yes" : "No"}
                          color={reward.is_claimed ? "success" : "default"}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Is Credited
                      </Typography>
                      <Box>
                        <Chip
                          label={reward.is_credited ? "Yes" : "No"}
                          color={reward.is_credited ? "success" : "default"}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">
                        Proof Status
                      </Typography>
                      <Box>{getProofStatusChip(reward.proof_status)}</Box>
                    </Grid>
                    {reward.unlocked_at && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Unlocked At
                        </Typography>
                        <Typography variant="body2">
                          {moment(reward.unlocked_at).format("DD-MM-YYYY hh:mm A")}
                        </Typography>
                      </Grid>
                    )}
                    {reward.minutes_required > 0 && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Minutes Required
                        </Typography>
                        <Typography variant="body2">{reward.minutes_required}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Card>
    </Modal>
  );
};

export default ViewDetailsModal;
